import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Super-admin copilot capability-gaps backlog — Module #34.
 *
 * Reads /v1/copilot/capability-gaps (super_admin only): the cross-tenant
 * backlog of asks the copilot flagged it couldn't fulfill, clustered by
 * suggested tool name. This is the "development opportunities" list Jim works
 * with Claude to turn into shipped tools (memory: feedback_copilot_gap_to_prod_loop).
 * Triage each cluster (planned / shipped / dismissed / duplicate) inline.
 */

const STATUSES = ['new', 'planned', 'staging', 'shipped', 'dismissed', 'duplicate'] as const;
type GapStatus = (typeof STATUSES)[number];
const FILTERS: readonly { key: GapStatus | 'all'; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'planned', label: 'Planned' },
  { key: 'staging', label: 'Staging' },
  { key: 'shipped', label: 'Shipped (prod)' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'duplicate', label: 'Duplicate' },
  { key: 'all', label: 'All' },
];

interface GapCluster {
  canonical_key: string | null;
  suggested_tool_name: string | null;
  category_type: string | null;
  category_area: string | null;
  gap_count: number;
  tenant_count: number;
  last_asked: string;
  max_confidence: number | null;
  sample_summaries: string[];
  gap_ids: number[];
}

interface GapsResponse {
  filters: { status: string; min_confidence: number };
  total_new: number;
  clusters: GapCluster[];
}

const TYPE_OPTIONS = [
  'new_action',
  'data_access',
  'draft_type',
  'integration',
  'report',
  'other',
] as const;
const AREA_OPTIONS = [
  'auctions',
  'lots',
  'inventory',
  'bidders',
  'invoices',
  'consignors',
  'comms',
  'reporting',
  'other',
] as const;

const facetSelectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: '0.4rem',
  border: '1px solid var(--border)',
  background: 'var(--surface-elev)',
  color: 'var(--text)',
  fontWeight: 600,
  fontSize: '0.85rem',
  cursor: 'pointer',
};

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: GapsResponse }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function SuperCopilotGaps() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [statusFilter, setStatusFilter] = useState<GapStatus | 'all'>('new');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [areaFilter, setAreaFilter] = useState<string>('');
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }
    setState({ kind: 'loading' });
    const qs = new URLSearchParams({ status: statusFilter });
    if (typeFilter) qs.set('type', typeFilter);
    if (areaFilter) qs.set('area', areaFilter);
    const url = `${base}/v1/copilot/capability-gaps?${qs.toString()}`;
    fetchWithAuth(url)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as GapsResponse;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          status: 0,
          message: err instanceof Error ? err.message : 'unknown error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, typeFilter, areaFilter, refreshNonce, userId]);

  async function applyTriage(gapIds: number[], status: GapStatus, notes: string): Promise<void> {
    const base = ieBaseUrl();
    if (!base || !session?.access_token) return;
    const res = await fetch(`${base}/v1/copilot/capability-gaps`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        gap_ids: gapIds,
        status,
        ...(notes.trim().length > 0 && { notes: notes.trim() }),
      }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as ApiError | null;
      throw new Error(body?.detail ?? `HTTP ${res.status}`);
    }
    setRefreshNonce((n) => n + 1);
  }

  const totalNew = state.kind === 'ok' ? state.data.total_new : null;

  return (
    <main style={{ padding: '2rem', maxWidth: '76rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Copilot capability gaps
            {totalNew !== null && totalNew > 0 && (
              <Badge variant="outline" style={{ marginLeft: '0.6rem', verticalAlign: 'middle' }}>
                {totalNew} new
              </Badge>
            )}
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
            <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
              ← Super dashboard
            </Link>
          </p>
        </div>
        <SignOutButton redirectTo="/super" />
      </div>

      <p
        style={{
          color: 'var(--text-muted)',
          fontSize: '0.9rem',
          marginBottom: '1.25rem',
          maxWidth: '52rem',
        }}
      >
        Asks the copilot flagged it couldn&apos;t fulfill, clustered by the tool that would close
        them. Highest-volume first — that&apos;s the build priority. Triage a cluster, then we build
        the tool on staging, test, and ship.
      </p>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            type="button"
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: '0.4rem',
              border: '1px solid var(--border)',
              background: statusFilter === f.key ? 'var(--brand-primary)' : 'var(--surface-elev)',
              color: statusFilter === f.key ? 'var(--brand-primary-fg)' : 'var(--text)',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filter by type"
          style={facetSelectStyle}
        >
          <option value="">All types</option>
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value)}
          aria-label="Filter by area"
          style={facetSelectStyle}
        >
          <option value="">All areas</option>
          {AREA_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          type="button"
          disabled={state.kind === 'loading'}
          style={{
            padding: '0.4rem 0.9rem',
            borderRadius: '0.4rem',
            border: '1px solid var(--border)',
            background: 'var(--surface-elev)',
            color: 'var(--text)',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: state.kind === 'loading' ? 'wait' : 'pointer',
            marginLeft: 'auto',
          }}
        >
          {state.kind === 'loading' ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {state.kind === 'idle' || state.kind === 'loading' ? (
        <Card>
          <CardContent>
            <p style={{ color: 'var(--text-muted)' }}>
              {state.kind === 'idle' ? 'Initializing…' : 'Loading capability gaps…'}
            </p>
          </CardContent>
        </Card>
      ) : state.kind === 'error' ? (
        <Card>
          <CardHeader>
            <CardTitle>Capability gaps unavailable</CardTitle>
            <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
          </CardHeader>
          <CardContent>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                color: 'var(--semantic-danger)',
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: '0.85rem',
                margin: 0,
              }}
            >
              {state.message}
            </pre>
          </CardContent>
        </Card>
      ) : state.data.clusters.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Nothing here</CardTitle>
            <CardDescription>
              No capability gaps match this filter. When the copilot hits something it can&apos;t
              do, it lands here.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {state.data.clusters.map((c, i) => (
            <GapClusterCard
              key={c.canonical_key ?? c.suggested_tool_name ?? `unspecified-${i}`}
              cluster={c}
              onApply={applyTriage}
            />
          ))}
        </div>
      )}
    </main>
  );
}

function GapClusterCard({
  cluster,
  onApply,
}: {
  cluster: GapCluster;
  onApply: (gapIds: number[], status: GapStatus, notes: string) => Promise<void>;
}) {
  const [status, setStatus] = useState<GapStatus>('planned');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function apply(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await onApply(cluster.gap_ids, status, notes);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card>
      <CardHeader>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <CardTitle>
              {cluster.canonical_key ?? cluster.suggested_tool_name ?? (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Uncategorized
                </span>
              )}
            </CardTitle>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', margin: '0.3rem 0' }}>
              {cluster.category_type !== null && (
                <Badge variant="outline">{cluster.category_type}</Badge>
              )}
              {cluster.category_area !== null && (
                <Badge variant="outline">{cluster.category_area}</Badge>
              )}
            </div>
            <CardDescription>
              {cluster.gap_count} ask{cluster.gap_count === 1 ? '' : 's'} · {cluster.tenant_count}{' '}
              tenant
              {cluster.tenant_count === 1 ? '' : 's'} · last asked {cluster.last_asked.slice(0, 10)}
              {cluster.max_confidence !== null && (
                <> · max confidence {Math.round(cluster.max_confidence * 100)}%</>
              )}
            </CardDescription>
          </div>
          <Badge variant="outline">{cluster.gap_count}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ul
          style={{
            margin: '0 0 1rem',
            paddingLeft: '1.1rem',
            color: 'var(--text)',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          {cluster.sample_summaries.map((s, i) => (
            <li key={i} style={{ marginBottom: '0.25rem' }}>
              {s}
            </li>
          ))}
        </ul>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as GapStatus)}
            disabled={busy}
            style={{
              padding: '0.4rem 0.6rem',
              borderRadius: '0.4rem',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: '0.85rem',
            }}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            disabled={busy}
            style={{
              flex: 1,
              minWidth: '12rem',
              padding: '0.4rem 0.6rem',
              borderRadius: '0.4rem',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: '0.85rem',
            }}
          />
          <button
            type="button"
            onClick={() => void apply()}
            disabled={busy}
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: '0.4rem',
              border: 'none',
              background: 'var(--brand-primary)',
              color: 'var(--brand-primary-fg)',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Applying…' : `Mark ${cluster.gap_count}`}
          </button>
        </div>
        {err !== null && (
          <p style={{ color: 'var(--semantic-danger)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
            {err}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
